import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostBinding, Input, Output, Renderer2, ViewEncapsulation, inject } from '@angular/core'
import { NgElement, WithProperties } from '@angular/elements'
import { DecodeHtmlStringPipe } from '../../pipes'
import { registerCustomElementFactory } from '../../custom-elements/register-custom-element-factory'

/**
 * @public
 */
export type AlertElement = NgElement & WithProperties<AlertComponent>

/**
 * @public
 */
@Component({
  selector: 'gpei-alert',
  templateUrl: './alert.component.html',
  styleUrls: [ './alert.component.sass' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: true,
  imports: [
    CommonModule,
    DecodeHtmlStringPipe
  ]
})
export class AlertComponent {

  private readonly _renderer2 = inject(Renderer2)
  private readonly _elementRef = inject(ElementRef)

  private _visible = true
  private _issues: string[]

  @Input()
  @HostBinding()
  type: string

  @Input()
  @HostBinding()
  message: string

  @Input()
  @HostBinding()
  closeable = true

  @Output() dismiss: EventEmitter<Event> = new EventEmitter()

  /**
   * Lifecycle event for when the alert component is closed. When the alert is closed,
   * it will set the visibility of the component to false, thus preventing it from rendering.
   *
   * @param event  Event from the `onClick` of the close button
   */
  onDismiss(event: Event): void {
    this.dismiss.emit(event)
    this.visible = false
  }

  get typeClasses(): Record<string, boolean> {
    let value = this?.type?.toUpperCase()
    return {
      'alert': true,
      'alert-danger': value === 'ERROR',
      'alert-success': value === 'SUCCESS',
      'alert-info': value === 'INFO',
      'alert-warning': value === 'WARNING'
    }
  }

  @Input()
  @HostBinding()
  set visible(value: boolean) {
    this._visible = value ?? true

    if (this._visible) {
      this._renderer2.removeStyle(this._elementRef.nativeElement, 'display')
    }

    else {
      this._renderer2.setStyle(this._elementRef.nativeElement, 'display', 'none')
    }
  }

  get visible(): boolean {
    return this._visible
  }

  @Input()
  @HostBinding()
  set issues(value: string | string[]) {
    if (typeof value === 'string' && value.trim() !== '') {
      this._issues = JSON.parse(value)
    }

    else if (value instanceof Array) {
      this._issues = value
    }
  }

  get issues(): string[] {
    return this._issues
  }

}

/**
 * @public
 */
export const registerAlertElement = registerCustomElementFactory(AlertComponent)
